'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';
// Import-free predicate, shared with global-error.tsx and every segment
// boundary. See its header for why a pure function is an acceptable dependency
// here where a component is not.
import { isTransientNetworkError } from '@/lib/errors/transient-network';
// Import-free too: the retry latch that survives Next remounting this screen,
// and the chunk-failure report Sentry's inbound filter does not drop.
import {
  boundaryRetryAvailable,
  isChunkLoadError,
  noteBoundaryRetry,
  releaseBoundaryRetry,
  recoverFromChunkFailure,
  RETRY_GRACE_MS,
  silentRetry,
  tryAgain,
} from '@/lib/errors/boundary-recovery';
// Import-free as well: whether a live call is holding off a reload, which a
// refresh that cannot connect would turn into (MUCKA-WEB-CH).
import { onReloadsReleased, reloadsHeld } from '@/lib/reload-guard';
// Import-free as well: whether one of our own full-page loads is already
// replacing the page, so a chunk boundary never starts a second (MUCKA-WEB-CV).
import { notePageLeaving, pageLeaving } from '@/lib/page-leaving';

/**
 * Root error boundary — the one that keeps the app alive.
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 *
 * Until it did, an engineer who lost signal and tapped a nav item had to
 * force-quit Mucka to get back in. The chain was:
 *
 *   1. The tap ran a client navigation into a dead network. If the RSC
 *      response died mid-stream (which is what a dropping connection does, as
 *      opposed to airplane mode) React threw during render rather than falling
 *      back to a browser navigation.
 *   2. Route groups add no path segment, so `(authed)/error.tsx` wraps the
 *      segment's CHILDREN, not `(authed)/layout.tsx` itself. Anything thrown by
 *      the layout — which is where AuthedShell mounts OfflineBanner,
 *      OfflineOverlay and OfflineSync — sailed straight past it.
 *   3. With nothing else above, it landed on `global-error.tsx`, which by
 *      design replaces the entire document. That unmounted every offline
 *      surface, the saved-work overlay and the sync loop along with them.
 *   4. Its only escape was `reset()`, which re-renders the same tree against
 *      the same dead network and throws again. Hence the force-quit.
 *
 * This boundary sits inside the root layout, so `<html>`/`<body>`, the inline
 * white-screen watchdog and the root-level mounts all survive, and recovery is
 * offered in place.
 *
 * ── DELIBERATELY DEPENDENCY-LIGHT ───────────────────────────────────────────
 *
 * Inline styles, no imports beyond React, Sentry and one import-free
 * predicate, no UI primitives. One of the errors this catches is a
 * ChunkLoadError — a lazy chunk that isn't in the cache and can't be fetched.
 * A recovery screen that needs to load a chunk to render is no recovery screen
 * at all.
 */
export default function AppError({
  error,
  reset,
  retry,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  /** Next's own refresh-then-reset: the only one that fetches the page again. */
  retry?: () => void;
}) {
  /**
   * One silent re-render for a dropped connection before anybody reads an
   * error screen. The segment boundaries have done this since #1983; this one
   * did not, and neither did global-error.tsx — so the two boundaries that
   * replace the MOST were the two with no second chance.
   *
   * 🚨 Bounded to ONE attempt. A boundary that retries on a loop turns a
   * deterministic failure into a spinner nobody can escape, and the failures
   * worth showing are exactly the ones that repeat.
   *
   * 🚨 The bound lives OUTSIDE this component. Next unmounts this screen to
   * render the page again, so if the page throws again a new screen mounts
   * with fresh state — state alone retried for ever behind a blank screen
   * (MUCKA-WEB-A2). The module latch is read once, at mount, and survives that.
   *
   * A chunk that failed to load is never retried this way: `React.lazy` keeps
   * its rejected import and throws it straight back without fetching.
   */
  // Set once the retry has been in flight for RETRY_GRACE_MS: still a
  // connection, not a fault, so it gets "Reconnecting…" and no report.
  const [stillWaiting, setStillWaiting] = useState(false);
  // A call is holding reloads, so the retry waits for it to end before it
  // fetches (`silentRetry`). Read once at mount, like the retry latch.
  const [callHeld] = useState(() => reloadsHeld());
  const [retryAvailable] = useState(() => boundaryRetryAvailable('root'));
  const chunk = isChunkLoadError(error);
  const transient = !chunk && isTransientNetworkError(error);
  const willRetry = transient && retryAvailable;
  const waitingOnCall = willRetry && callHeld;
  // A chunk's second chance is a reload, once (MUCKA-WEB-CV — see
  // `recoverFromChunkFailure`). Blank until it is spent, refused or overdue.
  const [chunkScreen, setChunkScreen] = useState(false);
  const chunkHandled = useRef<unknown>(null);

  useEffect(() => {
    if (!willRetry) return;
    noteBoundaryRetry('root');
    // Fetch the page again, not merely render it: `reset()` alone re-reads
    // the dead stream that threw (MUCKA-WEB-CH). During a call, once it ends.
    const cancel = silentRetry(retry, reset, { reloadsHeld, onReloadsReleased });
    // What un-blanks the screen if the retry neither lands nor fails.
    // `retry()` is a transition, and this screen stays mounted until the
    // fresh page arrives (see RETRY_GRACE_MS).
    const timer = setTimeout(() => setStillWaiting(true), RETRY_GRACE_MS);
    return () => {
      clearTimeout(timer);
      cancel();
      // Held for as long as this screen is up, however long the fetch takes.
      releaseBoundaryRetry('root');
    };
  }, [willRetry, retry, reset]);

  useEffect(() => {
    if (willRetry) return;
    // A captured ChunkLoadError is dropped at ingest by Sentry's chunk-load
    // filter, so this boundary's reports of one never arrived. Say it as a
    // message the filter keeps.
    if (chunk) {
      // Once per error: StrictMode's rehearsal must not spend the reload.
      if (chunkHandled.current === error) return;
      chunkHandled.current = error;
      recoverFromChunkFailure(Sentry, 'root', error, reloadsHeld, () => setChunkScreen(true), { pageLeaving, notePageLeaving });
      return;
    }
    // Tagged, not bare. MUCKA-WEB-35 sat on the board as an unresolved
    // "TypeError: Load failed" that took a code read to identify as this
    // boundary doing exactly its job — catching a navigation into a dead
    // network and offering recovery in place. `navigator.onLine` is the split
    // that makes the issue triageable from the list: offline is the designed
    // path, online is a real defect. Same reasoning as the app_state tag on
    // the iOS content-process reports (#1496) — a fact in context is
    // unsearchable, and opening every event is how a real fault stays buried
    // under routine ones.
    Sentry.captureException(error, {
      tags: {
        error_boundary: 'root',
        // 'recovered' never reaches here (we return above); the tag marks the
        // ones the user actually SAW, so the ratio stays readable in Sentry.
        boundary_outcome: transient ? 'shown_after_retry' : 'shown',
        // `onLine === false` is conclusive; true only means the interface is
        // up, so it is reported as-is rather than as "was online".
        navigator_online: typeof navigator === 'undefined' ? 'unknown' : String(navigator.onLine),
        boundary_route: typeof window === 'undefined' ? 'unknown' : window.location.pathname,
      },
    });
  }, [error, willRetry, transient, chunk]);

  // A reload that has not replaced the page by now is not going to on its own.
  useEffect(() => {
    if (!chunk || chunkScreen) return;
    const timer = setTimeout(() => setChunkScreen(true), RETRY_GRACE_MS);
    return () => clearTimeout(timer);
  }, [chunk, chunkScreen]);

  // Nothing while the page reloads for a chunk: it is about to be replaced.
  if (chunk && !chunkScreen) return null;

  // Nothing while the retry is in flight, rather than flashing the error
  // screen and taking it away again — and an honest word if it takes a while.
  // No "Try again" here: one is already running.
  if (willRetry) {
    // Waiting on a call is said at once: nothing will happen until it ends.
    if (!stillWaiting && !waitingOnCall) return null;
    return (
      <div style={wrap}>
        <div style={mark}>MUCKA</div>
        <h1 style={title}>{waitingOnCall ? 'This will load after your call' : 'Reconnecting…'}</h1>
        <p style={body}>
          {waitingOnCall
            ? <>The page dropped while it was loading. It&apos;ll load again as soon
              as the call ends — nothing you&apos;ve saved has been lost.</>
            : <>The signal dropped while this was loading. It&apos;ll appear as soon as
              it&apos;s back — nothing you&apos;ve saved has been lost.</>}
        </p>
        <button type="button" onClick={() => { window.location.href = '/offline'; }} style={quiet}>
          See your saved work
        </button>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={mark}>MUCKA</div>
      <h1 style={title}>That didn&apos;t load</h1>
      <p style={body}>
        This can happen when the signal drops mid-tap. Nothing you&apos;ve saved
        has been lost.
      </p>

      {/* For a chunk that would not load, "again" has to mean a fresh document:
          `reset()` re-renders the lazy component, which throws its cached
          failure straight back and fetches nothing. */}
      <button type="button" onClick={chunk ? () => window.location.reload() : () => tryAgain(retry, reset, reloadsHeld)} style={primary}>
        Try again
      </button>
      {/* A real reload, not a re-render. `reset()` runs the same tree against
          the same conditions, so on a dead connection it simply throws again —
          which is what made this screen a trap. A reload goes through the
          service worker, which can answer from cache. */}
      <button type="button" onClick={() => window.location.reload()} style={secondary}>
        Reload Mucka
      </button>
      {/* A hard navigation on purpose: a client push would fetch an RSC payload
          and die exactly the way we just did. This one reaches the service
          worker, which serves the saved-work page from cache. */}
      <button
        type="button"
        onClick={() => { window.location.href = '/offline'; }}
        style={quiet}
      >
        See your saved work
      </button>

      {error.digest && <div style={digest}>Reference: {error.digest}</div>}
    </div>
  );
}

const wrap: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: 24,
  background: '#231f20',
  color: '#eae9e8',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const mark: React.CSSProperties = {
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: '0.06em',
  color: '#ff4e00',
  marginBottom: 14,
};

const title: React.CSSProperties = { fontSize: 18, fontWeight: 700, margin: '0 0 6px' };

const body: React.CSSProperties = {
  fontSize: 14,
  color: 'rgba(234,233,232,0.6)',
  maxWidth: 300,
  lineHeight: 1.55,
  margin: '0 0 28px',
};

/** Chamfered, per the brand silhouette — never border-radius. */
const chamfer =
  'polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)';

const buttonBase: React.CSSProperties = {
  border: 'none',
  padding: '12px 28px',
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  clipPath: chamfer,
  minWidth: 210,
  marginBottom: 10,
};

const primary: React.CSSProperties = { ...buttonBase, background: '#ff4e00', color: '#231f20' };

const secondary: React.CSSProperties = {
  ...buttonBase,
  background: 'rgba(234,233,232,0.12)',
  color: '#eae9e8',
};

const quiet: React.CSSProperties = {
  ...buttonBase,
  background: 'transparent',
  color: 'rgba(234,233,232,0.6)',
  textTransform: 'none',
  letterSpacing: 0,
  fontWeight: 600,
};

const digest: React.CSSProperties = {
  marginTop: 18,
  fontSize: 11,
  color: 'rgba(234,233,232,0.35)',
};
